/**
 * A paper the student asked for: wait while it is written (the StudyScreen loop), then the
 * PaperView desk over the generation service's document — the service's own disclaimer on
 * the sheet, the notices that say what went wrong or was corrected, and an aside saying how
 * the paper was made (design v2 screen 3; API_CHANGES 4–6).
 */
import type { ApiFailure } from "../api/client";
import { FailedPage, LoadingPage, transient } from "../states/Page";
import { useEffect, useState } from "react";
import type { Api, PaperRow } from "../api/api";
import { RatingStrip } from "../feedback/RatingStrip";
import { reconciliationNotes } from "../paper/marks";
import { chapterSpan } from "./chapters";
import { apiMessage, paperFailure } from "../text/errors";
import { PaperView } from "../paper/PaperView";
import type { PaperDoc } from "../paper/types";
import "../styles/paper.css";
import { isPaperMoving } from "./describe";
import { madeOn, paperLabel } from "./identity";

type Props = {
  api: Api;
  paperId: string;
  sleep?: (ms: number) => Promise<void>;
  onBack: (subjectId: string) => void;
};

/** "Chapter 12", "Chapters 12 and 13", "Chapters 1, 2 and 4", "Chapters 1–20". */
export function chapterList(numbers: number[]): string {
  if (numbers.length === 1) return `Chapter ${numbers[0]}`;
  const parts = chapterSpan(numbers).split(", ");
  const last = parts.pop();
  return parts.length > 0 ? `Chapters ${parts.join(", ")} and ${last}` : `Chapters ${last}`;
}

/** API_CHANGES 4: what the accuracy pass checked, and which sections kept their draft. */
export function checkedLines(p: PaperDoc["provenance"]): string[] {
  const written = p.sections - p.sections_failed;
  const drafts = p.used_draft.length;
  const lines = [`${written - drafts} of ${written} sections checked.`];
  if (drafts > 0) {
    lines.push(
      drafts === 1
        ? "1 kept its first draft because the check did not finish."
        : `${drafts} kept their first drafts because the check did not finish.`,
    );
  }
  return lines;
}

function About({ row, doc, api }: { row: PaperRow; doc: PaperDoc; api: Api }) {
  const chapters = doc.provenance.chapters?.map((c) => c.number) ?? row.chapters;
  const format = row.format ? `${row.format.board} ${row.format.paper_title}`.trim() : "";
  return (
    <>
      <p className="paper-aside__kicker">About this paper</p>
      <dl className="paper-aside__facts">
        <div>
          <dt>Written from</dt>
          <dd>
            {chapters.length > 0 ? `${chapterList(chapters)} of your textbook` : doc.coverage}
          </dd>
        </div>
        {format && (
          <div>
            {/* No date: profiles keep no confirmed_at, and a paper needs a confirmed one. */}
            <dt>Format learned from</dt>
            <dd>{format}, as you confirmed it</dd>
          </div>
        )}
        <div>
          <dt>Checked</dt>
          {checkedLines(doc.provenance).map((line) => (
            <dd key={line}>{line}</dd>
          ))}
        </div>
      </dl>
      <p className="paper-aside__note">
        Written by AI. The mark scheme is a good guide, not an examiner. If an answer seems right to
        you and the scheme disagrees, check the chapter.
      </p>
      <RatingStrip api={api} kind="paper" refId={row.id} label={`Was ${paperLabel(row)} useful?`} />
    </>
  );
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const POLL_MS = 5000;
const MAX_POLLS = 240; // twenty minutes; a paper is a few

export function PaperScreen({ api, paperId, sleep = realSleep, onBack }: Props) {
  const [row, setRow] = useState<PaperRow | null>(null);
  const [doc, setDoc] = useState<PaperDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped by a retry, so the wait starts again for the new run.
  const [run, setRun] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [subject, setSubject] = useState<string | null>(null);

  const [failed, setFailed] = useState<ApiFailure | null>(null);

  // The tab says which paper it is: "Physics · Paper 3".
  useEffect(() => {
    if (!row) return;
    const before = document.title;
    document.title = subject ? `${subject} · ${paperLabel(row)}` : paperLabel(row);
    return () => {
      document.title = before;
    };
  }, [row, subject]);

  useEffect(() => {
    let live = true;
    setFailed(null);
    void (async () => {
      for (let attempt = 0; live && attempt < MAX_POLLS; attempt++) {
        const r = await api.getPaper(paperId);
        if (!live) return;
        if (!r.ok) return transient(r) ? setFailed(r) : setError(apiMessage(r.error));
        setRow(r.data);
        if (attempt === 0) {
          // The back link's name only; a failure leaves it saying "Back".
          const sid = r.data.subject_id;
          void api.listSubjects().then((l) => {
            const found = l.ok ? l.data.subjects.find((x) => x.id === sid) : undefined;
            if (live && found) setSubject(found.name);
          });
        }
        if (r.data.status === "ready") {
          const d = await api.getPaperDoc(paperId);
          if (!live) return;
          if (d.ok) setDoc(d.data);
          else if (transient(d)) setFailed(d);
          else setError(apiMessage(d.error));
          return;
        }
        if (!isPaperMoving(r.data)) return;
        await sleep(POLL_MS);
      }
    })();
    return () => {
      live = false;
    };
  }, [api, paperId, sleep, run]);

  async function retry() {
    setRetrying(true);
    const r = await api.retryPaper(paperId);
    setRetrying(false);
    if (!r.ok) return setError(apiMessage(r.error));
    setRow(r.data);
    setRun((n) => n + 1);
  }

  if (failed) return <FailedPage failure={failed} onRetry={() => setRun((n) => n + 1)} />;
  if (error) return <p role="alert">{error}</p>;
  if (!row) return <LoadingPage />;
  if (row.status !== "ready") {
    return (
      <main className="narrow">
        <p className="eyebrow">
          {subject ? `${subject} · ` : ""}
          {paperLabel(row)}
        </p>
        <h1>
          {row.status === "failed" ? "This paper could not be written" : "Writing your paper…"}
        </h1>
        {row.status === "failed" ? (
          <>
            <p className="muted">{paperFailure(row.error)}</p>
            {/* Every failure releases its hold (API_CHANGES 2), so this is true of any failure. */}
            <p>This didn't use your allowance. A retry uses a paper only if it finishes.</p>
            <div className="row">
              <button
                className="button button--primary"
                disabled={retrying}
                onClick={() => void retry()}
              >
                Retry
              </button>
              <button className="button" onClick={() => onBack(row.subject_id)}>
                Back
              </button>
            </div>
          </>
        ) : (
          <>
            {row.sections_total && row.sections_done ? (
              <p>
                Section {row.sections_done} of {row.sections_total} checked
              </p>
            ) : null}
            <p className="muted">
              Each section is drafted and then checked for accuracy. This takes a few minutes; you
              can leave and come back.
            </p>
            <button className="button" onClick={() => onBack(row.subject_id)}>
              Back
            </button>
          </>
        )}
      </main>
    );
  }
  if (!doc) return <LoadingPage />;
  const notes = reconciliationNotes(doc.provenance.reconciliation);
  return (
    <div className="paper-screen">
      <PaperView
        paper={doc}
        board={`${doc.board} ${doc.paper_title}`.trim() || undefined}
        disclaimer={doc.disclaimer}
        headingLevel={2}
        identity={{
          subject: subject ?? "Practice paper",
          number: row.number,
          ref: row.ref,
          name: row.name,
          made: madeOn(row.created_at),
        }}
        back={
          <button className="paper-screen__back" onClick={() => onBack(row.subject_id)}>
            ‹ {subject ?? "Back"}
          </button>
        }
        notices={
          <>
            {row.error && (
              <div className="notice notice--warn" role="status">
                <strong className="notice__title">{row.error}</strong>
                <p className="notice__next">
                  The paper is shorter than asked for; its marks still add up.
                </p>
              </div>
            )}
            {notes.corrected.map((line) => (
              <p key={line} className="paper-screen__note">
                {line}
              </p>
            ))}
          </>
        }
        aside={<About row={row} doc={doc} api={api} />}
      />
    </div>
  );
}
